import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAccount, useBalance, useReadContract, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { BIGCOINMAIN_ADDRESS, BIGCOINTOKEN_ADDRESS, TOKEN_SYMBOL } from "../../../utils/constants";
import { erc20Abi, formatUnits, parseUnits, zeroAddress } from "viem";
import { notifyError, notifySuccess } from "../../../hooks/utils";
import BigcoinTokenContractABI from "../../../abis/bigcointoken.abi.json";
import BigcoinMainContractABI from "../../../abis/bigcoinmain.abi.json";
import "./upgradespacemodal.css"
import { BigNumber } from "ethers";
import { useTranslation } from "react-i18next";

interface UpgradeSpaceModalProps {
  open: boolean;
  onClose: () => void;
  currentRoom: {
    id: number;
    name: string;
    maxMiners: number;
    maxPower: number;
    cost: number;
    description: string;
    img: string;
    x: number;
    y: number;
  };
  nextRoom: {
    id: number;
    name: string;
    maxMiners: number;
    maxPower: number;
    cost: number;
    description: string;
    img: string;
    x: number;
    y: number;
  };
}

const UpgradeSpaceModal: React.FC<UpgradeSpaceModalProps> = ({ open, onClose, currentRoom, nextRoom }) => {
  const { t, i18n } = useTranslation();
  const account = useAccount();

  const { data: bigTokenBalance } = useReadContract({
    address: BIGCOINTOKEN_ADDRESS,
    abi: BigcoinTokenContractABI,
    functionName: 'balanceOf',
    args: [account ? account.address : zeroAddress as any]
  });

  const bigTokenBalanceFormatted = useMemo(() => {
    if (bigTokenBalance) {
      return Number(formatUnits(bigTokenBalance as any, 18));
    }
    return 0;
  }, [bigTokenBalance]);

  const userHasEnoughBig = useMemo(() => {
    return bigTokenBalanceFormatted >= nextRoom.cost;
  }
    , [bigTokenBalanceFormatted, nextRoom.cost]);

  const [upgradingFacility, setUpgradingFacility] = useState(false);

  const { data: hash, error, isPending, writeContract } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  });

  // Log the transaction arguments and hash for debugging
  useEffect(() => {
    if (hash) {
      notifySuccess("Transaction submitted!");
    }
    if (error) {
      notifyError("An error occurred while trying to submit transaction.");
    }
    if (isConfirmed) {
      notifySuccess("Transaction confirmed!");
    }
  }, [hash, error]);


  const handleUpgradeSpace = async () => {
    if (!account) {
      notifyError("You need to connect wallet first.");
      return;
    }

    try {
      setUpgradingFacility(true);

      const transactionArgs = {
        address: BIGCOINMAIN_ADDRESS,
        abi: BigcoinMainContractABI,
        functionName: 'buyNewFacility',
        args:[]
      };

      writeContract(transactionArgs as any);
    } catch (error) {
      notifyError("An error occurred while trying to upgrade facility.");
    } finally {
      setUpgradingFacility(false);
    }
  };



  // Read the allowance and refetch on demand
  const { data: timeUntilNextFacilityUpgrade } = useReadContract({
    abi: BigcoinMainContractABI,
    address: BIGCOINMAIN_ADDRESS,
    functionName: "timeUntilNextFacilityUpgrade",
    args: [account ? account.address as any : zeroAddress],
    query: { refetchInterval: 1000 }
  });

  // print the time until next cooldown in format of HH:MM:SS (it comes in seconds)
  const timeUntilNextFacilityUpgradeFormatted = useMemo(() => {
    if (timeUntilNextFacilityUpgrade !== null && timeUntilNextFacilityUpgrade !== undefined) {
      const seconds = Number(timeUntilNextFacilityUpgrade.toString() as any);
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secondsLeft = seconds % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secondsLeft.toString().padStart(2, '0')}`;
    }
    return "00:00:00";
  }, [timeUntilNextFacilityUpgrade]);

  const canUpgradeToNextFacility = useMemo(() => {
    if (timeUntilNextFacilityUpgrade !== null && timeUntilNextFacilityUpgrade !== undefined) {
      const seconds = Number(timeUntilNextFacilityUpgrade.toString() as any);
      return seconds <= 0;
    }
    return false;
  }, [timeUntilNextFacilityUpgrade]);

  // Read the allowance and refetch on demand
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    abi: erc20Abi,
    address: BIGCOINTOKEN_ADDRESS,
    functionName: "allowance",
    args: [account ? account.address as any : zeroAddress, BIGCOINMAIN_ADDRESS],
  });

  const [hasSufficientAllowance, setHasSufficientAllowance] = useState(false);
  const [isApproving, setApproving] = useState(false);

  const handleApproveTokens = async () => {
    if (!account) {
      // Redirect to login or show a message
      notifyError("You need to connect wallet first.");
      return;
    }

    try {
      setApproving(true);

      const transactionArgs = {
        abi: erc20Abi,
        address: BIGCOINTOKEN_ADDRESS,
        functionName: 'approve',
        args: [BIGCOINMAIN_ADDRESS, parseUnits(nextRoom.cost.toString(), 18)],
      };

      writeContract(transactionArgs as any);
    } catch (error) {
      notifyError("An error occurred while trying to approve.");
    } finally {
      setApproving(false);
    }
  };
  useEffect(() => {
    if (allowance && nextRoom) {
      const isAllowed = BigNumber.from(allowance).gte(parseUnits(nextRoom.cost.toString(), 18));
      setHasSufficientAllowance(isAllowed);
    } else {
      setHasSufficientAllowance(false); // Reset when no allowance or trade value
    }
  }, [allowance, nextRoom]); // Update hasSufficientAllowance when allowance or trade value changes

  useEffect(() => {
    // Refetch allowance automatically when input trade value changes
    refetchAllowance();
  }, [nextRoom]); // Optionally refetch allowance when input value changes

  useEffect(() => {
    // Refetch allowance when the transaction is confirmed
    if (isConfirmed) {
      refetchAllowance();
    }
  }, [isConfirmed, refetchAllowance]);

  // MODAL OPEN AND CLOSE
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div data-state="open" className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ pointerEvents: "auto" }} data-aria-hidden="true" aria-hidden="true">
      <div
        ref={modalRef}
        role="dialog"
        id="radix-:r4f:"
        aria-describedby="radix-:r4h:"
        aria-labelledby="radix-:r4g:"
        data-state="open"
        className="text-sm fixed left-[50%] top-[50%] z-50 grid w-full max-w-modal translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-200 p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg dark:border-slate-800 dark:bg-slate-950 bg-primary text-white"
        tabIndex={-1}
        style={{ pointerEvents: "auto", border: "none", background: "rgb(16, 44, 57)" }}
      >


        <div className="sc-aa05954f-3 gnIWBN">
          <div className="sc-20379d03-0 jfiDP">
            <h2 className="sc-20379d03-1 jkOyLg">{t("upgrade_modal.title")}</h2>
            <div className="sc-20379d03-2 cSxpup">
              <p className="sc-20379d03-3 gKvA-DZ">
                {t("upgrade_modal.description")}
              </p>
            </div>
            <div className="sc-20379d03-4 gJlLGF">
              <div className="sc-20379d03-5 cfpMAt">
                <h3 className="sc-20379d03-6 eKBsQj">{t("upgrade_modal.current_space")}</h3>
                <div className="sc-20379d03-7 ezIGHp">
                  <img
                    src={currentRoom.img}
                    alt={"Your " + currentRoom.name}
                    className="sc-20379d03-15 goKlnn"
                  />
                </div>
                <div className="sc-20379d03-8 yKbsa">
                  <p className="sc-20379d03-9 jbvcbD">{t("upgrade_modal.your")} {currentRoom.name}</p>
                  <p className="sc-20379d03-9 jbvcbD">{t("upgrade_modal.max_animals")}: {currentRoom.maxMiners}</p>
                  <p className="sc-20379d03-9 jbvcbD">{t("upgrade_modal.food")}: {currentRoom.maxPower} {t("upgrade_modal.food")}</p>
                </div>
              </div>
              <div className="sc-20379d03-13 dqdIdd">→</div>
              <div className="sc-20379d03-5 cfpMAt">
                <h3 className="sc-20379d03-6 eKBsQj">{t("upgrade_modal.next_space")}</h3>
                <div className="sc-20379d03-7 ezIGHp">
                  <img
                    src={nextRoom.img}
                    alt={"Your " + nextRoom.name}
                    className="sc-20379d03-15 goKlnn"
                  />
                </div>
                <div className="sc-20379d03-8 yKbsa">
                  <p className="sc-20379d03-9 jbvcbD">{t("upgrade_modal.your")} {nextRoom.name}</p>
                  <p className="sc-20379d03-9 jbvcbD">{t("upgrade_modal.max_animals")}: {nextRoom.maxMiners}</p>
                  <p className="sc-20379d03-9 jbvcbD">{t("upgrade_modal.food")}: {nextRoom.maxPower} {t("upgrade_modal.food")}</p>
                </div>
              </div>
            </div>
            <div className="sc-20379d03-10 fwarbQ">
              <p className="sc-20379d03-11 hyoAkm">{t("upgrade_modal.upgrade_cost")}: {nextRoom.cost} ${TOKEN_SYMBOL}</p>
              {!userHasEnoughBig && (<p className="sc-20379d03-12 bHiJUs">{t("upgrade_modal.insufficient_milk")}</p>)}
            </div>
            <div className="sc-20379d03-14 emTiwP">
              <button className="sc-cdc39d4e-0 gvrgXY" onClick={onClose}>{t("upgrade_modal.cancel")}</button>


              {canUpgradeToNextFacility ? hasSufficientAllowance ? (
                <button onClick={handleUpgradeSpace} disabled={upgradingFacility || isPending || isConfirming || !userHasEnoughBig} className={`sc-cdc39d4e-0 ${upgradingFacility || isPending || isConfirming || !userHasEnoughBig ? "ivYpDu" : "glfaWa"}`}>
                  {(upgradingFacility || isPending || isConfirming) ? t("upgrade_modal.upgrading") : t("upgrade_modal.upgrade")}
                </button>
              ) : (
                <button onClick={handleApproveTokens} disabled={isApproving || isPending || isConfirming || !userHasEnoughBig} className={`sc-cdc39d4e-0 ${isApproving || isPending || isConfirming || !userHasEnoughBig ? "ivYpDu" : "glfaWa"}`}>
                  {(isApproving || isPending || isConfirming) ? t("upgrade_modal.approving") : t("upgrade_modal.approve")}
                </button>
              ) : (
                <button disabled className={`sc-cdc39d4e-0 ${"ivYpDu"}`}>
                  {`${timeUntilNextFacilityUpgradeFormatted}`}
                </button>
              )}
            </div>
          </div>
        </div>


      </div>
    </div>
  );
}
export default UpgradeSpaceModal;